'use client';

import { useState } from 'react';
import Image from 'next/image';
import type { Image as ImageType } from '@/lib/shopify/types';
import type { CustomDesignState } from '@/lib/customizer';
import { CustomizerPreviewOverlay } from './product-customizer';

interface ProductGalleryProps {
  images: ImageType[];
  productTitle: string;
  customDesign?: CustomDesignState;
}

function isShopifyImage(src: string) {
  try {
    const hostname = new URL(src).hostname;
    return hostname === 'cdn.shopify.com' || hostname.endsWith('.myshopify.com');
  } catch {
    return false;
  }
}

function getThumbnailUrl(src: string) {
  if (!isShopifyImage(src)) return src;

  const url = new URL(src);
  // Desktop thumbnails render at 80px. Asking Shopify for a 160px derivative
  // keeps them sharp on high-density displays without downloading the full
  // product image or making Next generate a much larger derivative.
  url.searchParams.set('width', '160');
  return url.toString();
}

export default function ProductGallery({ images, productTitle, customDesign }: ProductGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

  const displayImages = images.length > 0 ? images : [{ url: '', altText: 'No image' }];
  const selectedImage = displayImages[selectedIndex];

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isZoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Thumbnails - Desktop (vertical) */}
      <div className="hidden lg:flex flex-col gap-2 w-20 flex-shrink-0">
        {displayImages.map((image, index) => (
          <button
            key={index}
            onClick={() => setSelectedIndex(index)}
            className={`relative aspect-square bg-[var(--color-off-white)] overflow-hidden transition-all ${
              selectedIndex === index
                ? 'ring-2 ring-black'
                : 'ring-1 ring-[var(--color-border)] hover:ring-gray-400'
            }`}
          >
            {image.url ? (
              <Image
                src={getThumbnailUrl(image.url)}
                alt={image.altText || `${productTitle} thumbnail ${index + 1}`}
                fill
                className="object-cover"
                sizes="80px"
                loading={index < 3 ? 'eager' : 'lazy'}
                fetchPriority={index < 3 ? 'auto' : 'low'}
                unoptimized={isShopifyImage(image.url)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
                No image
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Main Image */}
      <div className="flex-1 relative">
        <div
          className="relative aspect-[3/4] bg-[var(--color-off-white)] overflow-hidden cursor-zoom-in"
          onMouseEnter={() => setIsZoomed(true)}
          onMouseLeave={() => setIsZoomed(false)}
          onMouseMove={handleMouseMove}
        >
          <div key={selectedIndex} className="absolute inset-0">
            {selectedImage.url ? (
              <>
                <Image
                  src={selectedImage.url}
                  alt={selectedImage.altText || productTitle}
                  fill
                  className="object-cover transition-transform duration-200"
                  style={{
                    transform: isZoomed ? 'scale(1.5)' : 'scale(1)',
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                  }}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
                {customDesign && selectedIndex === 0 && !isZoomed && (
                  <CustomizerPreviewOverlay design={customDesign} />
                )}
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                No image available
              </div>
            )}
          </div>

          {/* Navigation Arrows - Mobile */}
          {displayImages.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="lg:hidden absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white transition-colors"
                aria-label="Previous image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              <button
                onClick={handleNext}
                className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white transition-colors"
                aria-label="Next image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Thumbnails - Mobile (horizontal dots) */}
        {displayImages.length > 1 && (
          <div className="lg:hidden flex justify-center gap-2 mt-4">
            {displayImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setSelectedIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  selectedIndex === index ? 'bg-black w-6' : 'bg-gray-300'
                }`}
                aria-label={`View image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
